import { add, make as make$3, get as get$1 } from '../../Context/dist/effect-Context.esm.js';
import { suspend } from '../../Effect/dist/effect-Effect.esm.js';
import { pipe, dual } from '../../Function/dist/effect-Function.esm.js';
import { fiberRefUnsafeMakeContext, fiberRefGetWith, fiberRefLocallyWith, tap, map, sync, succeed, context, fiberRefLocally, as } from '../../internal/core.esm.js';
import { liveServices as liveServices$1, currentServices as currentServices$1 } from '../../internal/defaultServices.esm.js';
import { fiberRefLocallyScopedWith, fiberRefMake } from '../../internal/fiberRuntime.esm.js';
import { scoped } from '../../internal/layer.esm.js';
import { unsafeMake } from '../../internal/ref.esm.js';
import { empty } from '../../TestAnnotationMap/dist/effect-TestAnnotationMap.esm.js';
import { TestAnnotations, make as make$4 } from '../../TestAnnotations/dist/effect-TestAnnotations.esm.js';
import { TestConfig, make } from '../../TestConfig/dist/effect-TestConfig.esm.js';
import { TestLive, make as make$2 } from '../../TestLive/dist/effect-TestLive.esm.js';
import { TestSized, make as make$1, fromFiberRef } from '../../TestSized/dist/effect-TestSized.esm.js';

/**
 * @since 2.0.0
 */

/**
 * @since 2.0.0
 */

/**
 * The default Effect test services.
 *
 * @since 2.0.0
 */
const liveServices = /*#__PURE__*/pipe( /*#__PURE__*/make$3(TestAnnotations, /*#__PURE__*/make$4( /*#__PURE__*/unsafeMake( /*#__PURE__*/empty()))), /*#__PURE__*/add(TestLive, /*#__PURE__*/make$2(liveServices$1)), /*#__PURE__*/add(TestSized, /*#__PURE__*/make$1(100)), /*#__PURE__*/add(TestConfig, /*#__PURE__*/make({
  repeats: 100,
  retries: 100,
  samples: 200,
  shrinks: 1000
})));

/**
 * @since 2.0.0
 */
const currentServices = /*#__PURE__*/fiberRefUnsafeMakeContext(liveServices);

/**
 * Retrieves the `Annotations` service for this test.
 *
 * @since 2.0.0
 */
const annotations = () => annotationsWith(succeed);

/**
 * Retrieves the `Annotations` service for this test and uses it to run the
 * specified workflow.
 *
 * @since 2.0.0
 */
const annotationsWith = f => fiberRefGetWith(currentServices, services => f(get$1(services, TestAnnotations)));

/**
 * Executes the specified workflow with the specified implementation of the
 * annotations service.
 *
 * @since 2.0.0
 */
const withAnnotations = /*#__PURE__*/dual(2, (effect, annotations) => fiberRefLocallyWith(currentServices, add(TestAnnotations, annotations))(effect));

/**
 * Sets the implementation of the annotations service to the specified value
 * and restores it to its original value when the scope is closed.
 *
 * @since 2.0.0
 */
const withAnnotationsScoped = annotations => fiberRefLocallyScopedWith(currentServices, add(TestAnnotations, annotations));

/**
 * Constructs a new `Annotations` service wrapped in a layer.
 *
 * @since 2.0.0
 */
const annotationsLayer = () => scoped(TestAnnotations, pipe(sync(() => unsafeMake(empty())), map(make$4), tap(withAnnotationsScoped)));

/**
 * Accesses an `Annotations` instance in the context and retrieves the
 * annotation of the specified type, or its default value if there is none.
 *
 * @since 2.0.0
 */
const get = key => annotationsWith(annotations => annotations.get(key));

/**
 * Accesses an `Annotations` instance in the context and appends the
 * specified annotation to the annotation map.
 *
 * @since 2.0.0
 */
const annotate = (key, value) => annotationsWith(annotations => annotations.annotate(key, value));

/**
 * Returns the set of all fibers in this test.
 *
 * @since 2.0.0
 */
const supervisedFibers = () => annotationsWith(annotations => annotations.supervisedFibers());

/**
 * Retrieves the `Live` service for this test and uses it to run the specified
 * workflow.
 *
 * @since 2.0.0
 */
const liveWith = f => fiberRefGetWith(currentServices, services => f(get$1(services, TestLive)));

/**
 * Retrieves the `Live` service for this test.
 *
 * @since 2.0.0
 */
const live = /*#__PURE__*/liveWith(succeed);

/**
 * Executes the specified workflow with the specified implementation of the
 * live service.
 *
 * @since 2.0.0
 */
const withLive = /*#__PURE__*/dual(2, (effect, live) => fiberRefLocallyWith(currentServices, add(TestLive, live))(effect));

/**
 * Sets the implementation of the live service to the specified value and
 * restores it to its original value when the scope is closed.
 *
 * @since 2.0.0
 */
const withLiveScoped = live => fiberRefLocallyScopedWith(currentServices, add(TestLive, live));

/**
 * Constructs a new `Live` service wrapped in a layer.
 *
 * @since 2.0.0
 */
const liveLayer = () => scoped(TestLive, pipe(context(), map(make$2), tap(withLiveScoped)));

/**
 * Provides a workflow with the "live" default Effect services.
 *
 * @since 2.0.0
 */
const provideLive = effect => liveWith(live => live.provide(effect));

/**
 * Runs a transformation function with the live default Effect services while
 * ensuring that the workflow itself is run with the test services.
 *
 * @since 2.0.0
 */
const provideWithLive = /*#__PURE__*/dual(2, (self, f) => fiberRefGetWith(currentServices$1, services => provideLive(f(fiberRefLocally(currentServices$1, services)(self)))));

/**
 * Retrieves the `Sized` service for this test and uses it to run the
 * specified workflow.
 *
 * @since 2.0.0
 */
const sizedWith = f => fiberRefGetWith(currentServices, services => f(get$1(services, TestSized)));

/**
 * Retrieves the `Sized` service for this test.
 *
 * @since 2.0.0
 */
const sized = /*#__PURE__*/sizedWith(succeed);

/**
 * Executes the specified workflow with the specified implementation of the
 * sized service.
 *
 * @since 2.0.0
 */
const withSized = /*#__PURE__*/dual(2, (effect, sized) => fiberRefLocallyWith(currentServices, add(TestSized, sized))(effect));

/**
 * Sets the implementation of the sized service to the specified value and
 * restores it to its original value when the scope is closed.
 *
 * @since 2.0.0
 */
const withSizedScoped = sized => fiberRefLocallyScopedWith(currentServices, add(TestSized, sized));

/**
 * @since 2.0.0
 */
const sizedLayer = size => scoped(TestSized, pipe(fiberRefMake(size), map(fromFiberRef), tap(withSizedScoped)));

/**
 * @since 2.0.0
 */
const size = /*#__PURE__*/sizedWith(sized => sized.size());

/**
 * @since 2.0.0
 */
const withSize = /*#__PURE__*/dual(2, (effect, size) => sizedWith(sized => sized.withSize(size)(effect)));

/**
 * Retrieves the `TestConfig` service for this test and uses it to run the
 * specified workflow.
 *
 * @since 2.0.0
 */
const testConfigWith = f => fiberRefGetWith(currentServices, services => f(get$1(services, TestConfig)));

/**
 * Retrieves the `TestConfig` service for this test.
 *
 * @since 2.0.0
 */
const testConfig = /*#__PURE__*/testConfigWith(succeed);

/**
 * Executes the specified workflow with the specified implementation of the
 * config service.
 *
 * @since 2.0.0
 */
const withTestConfig = /*#__PURE__*/dual(2, (effect, config) => fiberRefLocallyWith(currentServices, add(TestConfig, config))(effect));

/**
 * Sets the implementation of the config service to the specified value and
 * restores it to its original value when the scope is closed.
 *
 * @since 2.0.0
 */
const withTestConfigScoped = config => fiberRefLocallyScopedWith(currentServices, add(TestConfig, config));

/**
 * Constructs a new `TestConfig` service with the specified settings.
 *
 * @since 2.0.0
 */
const testConfigLayer = params => scoped(TestConfig, suspend(() => {
  const testConfig = make(params);
  return pipe(withTestConfigScoped(testConfig), as(testConfig));
}));

/**
 * The number of times to repeat tests to ensure they are stable.
 *
 * @since 2.0.0
 */
const repeats = /*#__PURE__*/testConfigWith(config => succeed(config.repeats));

/**
 * The number of times to retry flaky tests.
 *
 * @since 2.0.0
 */
const retries = /*#__PURE__*/testConfigWith(config => succeed(config.retries));

/**
 * The number of sufficient samples to check for a random variable.
 *
 * @since 2.0.0
 */
const samples = /*#__PURE__*/testConfigWith(config => succeed(config.samples));

/**
 * The maximum number of shrinkings to minimize large failures.
 *
 * @since 2.0.0
 */
const shrinks = /*#__PURE__*/testConfigWith(config => succeed(config.shrinks));

export { annotate, annotations, annotationsLayer, annotationsWith, currentServices, get, live, liveLayer, liveServices, liveWith, provideLive, provideWithLive, repeats, retries, samples, shrinks, size, sized, sizedLayer, sizedWith, supervisedFibers, testConfig, testConfigLayer, testConfigWith, withAnnotations, withAnnotationsScoped, withLive, withLiveScoped, withSize, withSized, withSizedScoped, withTestConfig, withTestConfigScoped };
